import { ScreenManager } from "../managers/ScreenManager";
import { Showable } from "../mixins/Showable";
import { TextureData } from "../types/types";
import { ApplyMixins } from "../utils/ApplyMixins";
import { ScreenOrientationType } from "../utils/GetOrientation";
import { RectSize } from "../utils/RectSize";


export type SmartBackgroundProps = {
    logo?: {
        x?: number;
        y?: number;
        scale?: {
            base?: number;
            bias?: number;
        }
    },
    bg?: {
        update: {
            scrollX?: number;
            scrollY?: number;
        }
    }
}


export interface SmartBackground extends Showable { }

/**
 * Smart background with support of:
 * - tiles for bg
 * - centered logo
 * - 9sliced borders and corners 
 * 
 * @usage:
    this.bg = new SmartBackground(this.scene);
    // this.bg.SetProps({logo:{scale:{base:1, bias:0.5}}});
    this.bg.Init(
        {texture: LoaderFileKeys.lossless, frame: bgFrame}, // bg
        null, // logo
        {texture: LoaderFileKeys.ui, frame: "bg-borders-01.png"}, // borders
        4, // borderScale
    );
    this.bg.Build();
    this.add(this.bg);
 
 * Do not forget to externally call:
    this.Register
    this.Unregister
 */
export class SmartBackground
    extends Phaser.GameObjects.Container {
    private static appliedMixins: boolean = false;

    public tiles: Phaser.GameObjects.TileSprite;
    public logo: Phaser.GameObjects.Image;
    public nsBorders: Phaser.GameObjects.NineSlice;

    protected tileTextureData: TextureData;
    protected logoTextureData: TextureData;
    protected bordersTextureData: TextureData;
    protected bordersScale: number;
    protected defaultProps: SmartBackgroundProps = {
        logo: {
            x: 0,
            y: 0,
            scale: {
                base: 1,
                bias: 1,
            }
        },
        bg: {
            update: {
                scrollX: 0,
                scrollY: 0,
            }
        }
    }
    protected props: SmartBackgroundProps;


    public Init(
        tileTextureData: TextureData,
        logoTextureData: TextureData = undefined,
        bordersTextureData: TextureData = undefined,
        bordersScale: number = 1,
    ): void {
        // this.name = "bg";
        if (!SmartBackground.appliedMixins) {
            ApplyMixins(SmartBackground, [Showable]);
            SmartBackground.appliedMixins = true;
        }

        this.SetTweenProps(
            // showTweenConfig
            {
                duration: 500,
                onStart: this.OnShowStart.bind(this),
                // onStartParams: ,
                // onComplete: this.OnShowComplete.bind(this),
            },
            // hideTweenConfig
            {
                duration: 500,
                onStart: this.OnHideStart.bind(this),
            },
            // showTweenTargetProps
            {
                // alpha: 1,
                // y: ScreenManager.GetWindowRect().height,
            },
            // hideTweenTargetProps
            {
                // alpha: 0,
                // y: ScreenManager.GetWindowRect().height,
            },
            // fromShowTweenTargetProps
            {
                // alpha: 0,
            }
        );

        this.tileTextureData = tileTextureData;
        this.logoTextureData = logoTextureData;
        this.bordersTextureData = bordersTextureData;
        this.bordersScale = bordersScale;

        this.props = {
            ...this.defaultProps
        };
    }

    public Build(): void {
        // console.log(this.tileTextureData.texture, this.tileTextureData.frame)
        this.tiles = this.scene.make.tileSprite({
            width: 1,
            height: 1,
            key: this.tileTextureData.texture,
            frame: this.tileTextureData.frame,
        },
            false // addToScene?
        );
        this.tiles.setOrigin(0);
        this.add(this.tiles);

        if (this.logoTextureData != undefined) {
            this.logo = this.scene.make.image({ key: this.logoTextureData.texture, frame: this.logoTextureData.frame });
            this.add(this.logo);
        }

        if (this.bordersTextureData != undefined) {
            // original image width: 768, but downscale in TP to 25%;
            // original borderSize: 256, 25%=64 
            const borderSize = 64;
            this.nsBorders = this.scene.make.nineslice({
                key: this.bordersTextureData.texture,
                frame: this.bordersTextureData.frame,
                // width: 256, height: 256,
                leftWidth: borderSize, rightWidth: borderSize, topHeight: borderSize, bottomHeight: borderSize
            });
            this.nsBorders.setOrigin(0);
            this.nsBorders.setScale(this.bordersScale); // saves on texture space (later setSize to quarter screen size)
            this.add(this.nsBorders);
        }

        // control from outside!
        // this.RegisterListeners();
    }

    protected OnShowStart(): void {

    }

    protected OnHideStart(): void {

    }

    public SetTile(textureData: TextureData): void {
        this.tiles.setTexture(textureData.texture, textureData.frame);
    }

    public SetLogo(textureData: TextureData): void {
        this.logo.setTexture(textureData.texture, textureData.frame);
    }

    /**
     * 
     * @param logoScaleBase 
     * @param logoScaleBias 
     */
    public SetProps(props: SmartBackgroundProps): void {
        this.props = {
            ...this.props,
            ...props
        }
    }

    public GetProps(): SmartBackgroundProps {
        return this.props;
    }

    public GetDefaultProps(): SmartBackgroundProps {
        return this.defaultProps;
    }

    protected OnResizeManual(): void {
        const windowRect: RectSize = ScreenManager.GetWindowRect();
        this.OnResize(windowRect);
    }

    /**
     * Resize this component.
     * The reason why we do not autoresize (locally register to RESIZE) is that once we do, we forfeit control over unregistering such listener from outside (we should control both the registration and unregistration from the same scope) - and since we'll need to unregister from outside when the parent container goes invis for ex (optimization) - then its best that the parent also handles resizing of this component and that the parent registers to RESIZE (or even it's parent, etc up to root if necessary).
     * 
     * @param windowRect available screen space rect
     */
    public OnResize(windowRect: RectSize): void {
        // console.log("SmartBackgrounds::OnResize", this.name);

        const screenCenterX: number = (0.5 * windowRect.width) | 0;
        const screenCenterY: number = (0.5 * windowRect.height) | 0;
        const screenOrientationType: ScreenOrientationType = ScreenManager.GetOrientation();

        this.tiles.setSize(windowRect.width, windowRect.height);
        let tileScale: number;
        switch (screenOrientationType) {
            case ScreenOrientationType.LANDSCAPE:
                tileScale = ScreenManager.GetAssetScale(1.1, 1.0);
                break;
            case ScreenOrientationType.PORTRAIT:
                tileScale = ScreenManager.GetAssetScale(2.0, 1.0);
                break;
        }
        this.tiles.setTileScale(tileScale);

        if (this.bordersTextureData != undefined) {
            const inverseBordersScale = 1 / this.bordersScale;
            this.nsBorders.setSize(
                Math.ceil(windowRect.width * inverseBordersScale),
                Math.ceil(windowRect.height * inverseBordersScale)
            );
        }

        if (this.logo != undefined) {
            const logoScale = ScreenManager.GetAssetScale(this.props.logo.scale.base, this.props.logo.scale.bias);
            this.logo.scale = logoScale;
            this.logo.setPosition(screenCenterX, screenCenterY);
        }
    }

    /**
     * Call / control this from outside, just like OnResize (@see OnResize for reasoning)
     * 
     * time {number} The current time. Either a High Resolution Timer value if it comes from Request Animation Frame, or Date.now if using SetTimeout.
     * 
     * delta {number} The delta time in ms since the last frame. This is a smoothed and capped value based on the FPS rate.
     */
    public Update(time: number, delta: number): void {
        if (this.props.bg.update.scrollX) {
            this.tiles.tilePositionX += (this.props.bg.update.scrollX * delta);
        }
        if (this.props.bg.update.scrollY) {
            this.tiles.tilePositionY += (this.props.bg.update.scrollY * delta);
        }
    }
}